import * as actionTypes from '../actions/actionTypes/contextTypes';
import { IOrganizationContext } from '../entities/IContext';
import { EUserOrigin } from '../entities/ISoge';

export interface IContextOrganizations {
    ids: string[];
    byId: {
        [id: string]: IOrganizationContext;
    };
    byUrl: {
        [url: string]: IOrganizationContext;
    }
}

export interface IContextReducer {
    pageContext: {
        web: {
            absoluteUrl: string;
            language: number;
        };
        user?: {
            displayName: string;
            email: string;
            department?: string;
            userOrigin?: EUserOrigin;
        };
        oid?: string;
    },
    isLoggedIn: boolean;
    isShadowAuth: boolean;
    isInExportView: boolean;
    instanceId: string;
    tokenExpiration: number;
    token: string;
    refreshToken: string;
    contextSet: boolean;
    passwordResetMode: boolean;
    googlePlacesLoaded: boolean;
    tokenReloading: boolean;
    tokenRenewalRequested: boolean;
    contextOrganizations: IContextOrganizations;
    organizationsLoaded: boolean;
    hasActiveApprovalRequests: boolean;
}

const defaultState = (): IContextReducer => ({
    pageContext: {
        web: {
            absoluteUrl: location.origin,
            language: 1033 // TODO: for this moment it's hard coded to show only EN language
        },
        user: undefined,
        oid: undefined
    },
    isLoggedIn: false,
    tokenExpiration: undefined,
    isShadowAuth: false,
    isInExportView: false,
    instanceId: 'LegalCluster',
    token: undefined,
    refreshToken: undefined,
    contextSet: false,
    passwordResetMode: false,
    googlePlacesLoaded: false,
    tokenReloading: true,
    tokenRenewalRequested: false,
    contextOrganizations: {
        ids: [],
        byId: {},
        byUrl: {}
    },
    organizationsLoaded: false,
    hasActiveApprovalRequests: false
});

export default (state = defaultState(), action): IContextReducer => {
    switch (action.type) {
        case actionTypes.SET_LOGGED_IN_USER: {
            return {
                ...state,
                isLoggedIn: true,
                contextSet: true,
                token: action.accessToken,
                refreshToken: action.refresh_token,
                tokenExpiration: action.tokenExpiration,
                tokenReloading: false,
                pageContext: {
                    ...state.pageContext,
                    user: action.user,
                    oid: action.oid
                }
            };
        }

        case actionTypes.SET_PASSWORD_RESET_MODE: {
            return {
                ...state,
                passwordResetMode: action.passwordResetMode
            };
        }

        case actionTypes.CLEAR_USER_CONTEXT: {
            return {
                ...state,
                token: '',
                isLoggedIn: false,
                isShadowAuth: false,
                pageContext: {
                    ...state.pageContext,
                    user: undefined
                }
            };
        }

        case actionTypes.LOAD_EXTERNAL_GPLACES: {
            return {
                ...state,
                googlePlacesLoaded: true
            };
        }

        case actionTypes.SET_TOKEN_RELOAD_LOADER: {
            return {
                ...state,
                tokenReloading: action.loading
            };
        }

        case actionTypes.REQUEST_TOKEN_RENEWAL: {
            return {
                ...state,
                tokenRenewalRequested: true
            };
        }

        case actionTypes.LOAD_ORGANIZATION_CONTEXTS: {
            const organizations = (action.organizations || []) as IOrganizationContext[];

            return {
                ...state,
                organizationsLoaded: true,
                contextOrganizations: {
                    ids: organizations.map(organization => organization.id),
                    byId: organizations.reduce((organizationsById, organization: IOrganizationContext) => ({
                        ...organizationsById,
                        [organization.id]: organization
                    }), {}),
                    byUrl: organizations.reduce((organizationsByUrl, organization: IOrganizationContext) => ({
                        ...organizationsByUrl,
                        [organization.details.urlName]: organization
                    }), {})
                }
            };
        }

        case actionTypes.ADD_TAGS_TO_CONTEXT_ORGANIZATION: {
            const updatedOrg: IOrganizationContext = {
                ...state.contextOrganizations.byUrl[action.organizationUrl],
                details: {
                    ...state.contextOrganizations.byUrl[action.organizationUrl].details,
                    tags: [
                        ...(state.contextOrganizations.byUrl[action.organizationUrl].details.tags || []),
                        ...action.tags.map(tag => ({
                            name: tag,
                            status: 'Active'
                        }))
                    ]
                }
            };

            return {
                ...state,
                contextOrganizations: {
                    ...state.contextOrganizations,
                    byUrl: {
                        ...state.contextOrganizations.byUrl,
                        [updatedOrg.details.urlName]: updatedOrg
                    },
                    byId: {
                        ...state.contextOrganizations.byId,
                        [updatedOrg.id]: updatedOrg
                    }
                }
            };
        }

        case actionTypes.SET_ACCESS_TOKEN: {
            return {
                ...state,
                token: action.token,
                refreshToken: action.refresh_token,
                isLoggedIn: true,
                isShadowAuth: action.isShadowAuth,
                tokenExpiration: action.tokenExpiration
            };
        }

        case actionTypes.SET_EXPORT_VIEW: {
            return {
                ...state,
                isInExportView: action.isInExportView
            };
        }

        case actionTypes.SET_HAS_ACTIVE_APPROVAL_REQUESTS: {
            return {
                ...state,
                hasActiveApprovalRequests: action.hasActiveApprovalRequests
            };
        }

        default: return state;
    }
};